import { useState, useEffect, useRef } from 'react';
import { useGetScriptFromPrompt } from '../ai-script/useGetScriptFromPrompt';
import { useSnackbar } from 'notistack';
import { AI_SCRIPT_ERR_MSG, AI_SCRIPT_LIMIT_MSG } from '../../utils/constant';
import { useGenerateAndSaveAiText } from '../ai-script/useGenerateAndSaveAiText';
import { useRouter } from 'next/router';

export const useAiText = () => {
  const [prompt, setPrompt] = useState('');
  const [isGenerated, setIsGenerated] = useState(false);
  const [showDynamicContent, setShowDynamicContent] = useState(false);
  const [temporaryPrompt, setTemporaryPrompt] = useState('');
  const [displayedContent, setDisplayedContent] = useState('');
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const [showCreditDialog, setShowCreditDialog] = useState(false);
  const { mutate, isLoading } = useGenerateAndSaveAiText();
  const { enqueueSnackbar } = useSnackbar();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const contentRef = useRef('');
  const scrollPositionRef = useRef<number | null>(null);
  const { push } = useRouter();

  const handleClear = () => {
    setPrompt('');
    setDisplayedContent('');
    setIsGenerated(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };

  const handleClose = () => {
    setShowDynamicContent(false);
    setDisplayedContent('');
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };

  const handleCloseCreditDialog = () => {
    setShowCreditDialog(false);
  };

  const handleUpgrade = () => push('/subscription');

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      enqueueSnackbar('Prompt is required', { variant: 'error' });
      return;
    }
    mutate(
      {
        prompt: prompt.trim(),
      },
      {
        onSuccess: (res) => {
          if (res.includes(AI_SCRIPT_ERR_MSG)) {
            setPrompt('');
            enqueueSnackbar(res, { variant: 'error' });
            return;
          }
          if (res.includes(AI_SCRIPT_LIMIT_MSG)) {
            setPrompt('');
            setShowCreditDialog(true);
            return;
          }
          if (res.length) {
            setTemporaryPrompt(res);
            setShowDynamicContent(true);
            setIsGenerated(true);
            startDisplayingContent(res);
          }
        },
        onError: (error: any) => {
          enqueueSnackbar('Failed to generate script, Please try again.', {
            variant: 'error',
          });
        },
      }
    );
  };

  const handleScroll: React.UIEventHandler<HTMLDivElement> = (event) => {
    const target = event.target as HTMLDivElement;

    const currentScrollTop = target.scrollTop;

    if (scrollPositionRef.current !== null) {
      if (currentScrollTop > scrollPositionRef.current) {
      } else if (currentScrollTop < scrollPositionRef.current) {
        setIsUserScrolling(true);
      }
    }

    scrollPositionRef.current = currentScrollTop;
  };

  const startDisplayingContent = (content: string) => {
    let index = 0;
    contentRef.current = '';
    setDisplayedContent('');

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      if (index < content.length) {
        contentRef.current += content[index];
        setDisplayedContent(contentRef.current);
        index++;
      } else {
        clearInterval(intervalRef.current as NodeJS.Timeout);
      }
    }, 5);
  };

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const convertHtmlToPlainText = (htmlString: string): string => {
    const tempElement = document.createElement('div');
    tempElement.innerHTML = htmlString;
    return tempElement.textContent || tempElement.innerText || '';
  };

  const onCopy = () => {
    if (showDynamicContent) {
      const plainText = convertHtmlToPlainText(temporaryPrompt);
      navigator.clipboard
        .writeText(plainText)
        .then(() => {
          enqueueSnackbar('Text copied to clipboard!', { variant: 'success' });
        })
        .catch(() => {
          enqueueSnackbar('Failed to copy text', { variant: 'error' });
        });
    } else {
      enqueueSnackbar('No text to copy', { variant: 'error' });
    }
  };

  const downloadTxtFile = () => {
    const plainText = convertHtmlToPlainText(temporaryPrompt);
    const element = document.createElement('a');
    const file = new Blob([plainText], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = 'aiscript.txt';
    document.body.appendChild(element);
    element.click();
  };

  return {
    prompt,
    setPrompt,
    isGenerated,
    isLoading,
    handleClear,
    handleGenerate,
    showCreditDialog,
    handleCloseCreditDialog,
    handleUpgrade,
    onCopy,
    downloadTxtFile,
    showDynamicContent,
    handleClose,
    setShowDynamicContent,
    displayedContent,
    isUserScrolling,
    handleScroll,
    temporaryPrompt,
  };
};
